import React from "react";
import { Link } from "react-router-dom";
import {ArrowLeft, Syringe, DollarSign, Calendar, Shield} from 'lucide-react';
import { motion } from "framer-motion";
import BookingModal from "../../components/BookingModal";

const TravelVaccinesCanadaGuide: React.FC = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources" className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:gap-3 transition-all mb-8">
            <ArrowLeft className="w-5 h-5" />
            Back to Resources
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold">Vaccines</div>
              <span className="text-sm text-gray-500 dark:text-gray-400">8 min read</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Complete Guide to Travel Vaccines in Canada 2024
            </h1>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Everything you need to know about travel vaccines available in Canada, including costs, coverage, and booking procedures for international travelers.
            </p>

            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>Published: January 15, 2024</span>
              <span>•</span>
              <span>Updated: January 15, 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <img
            src="https://static.lumi.new/6a/6a6889e1c5dedfdfe340214de1f4eee9.jpeg"
            alt="Travel vaccines in Canada"
            className="w-full h-96 object-cover rounded-2xl mb-12"
          />

          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Understanding Travel Vaccines in Canada</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Planning international travel requires more than just booking flights and hotels. Protecting your health through proper vaccinations is a crucial step that many travelers overlook until it's too late. In Canada, we have access to a comprehensive range of travel vaccines, but understanding which ones you need, where to get them, and how much they cost can be overwhelming.
          </p>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Syringe className="w-6 h-6 text-cyan-500" />
            Common Travel Vaccines Available in Canada
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Canada offers a wide variety of travel vaccines to protect against diseases that may not be common domestically but pose risks in other parts of the world:
          </p>

          <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li><strong>Hepatitis A</strong> - One of the most commonly recommended travel vaccines, protecting against contaminated food and water</li>
            <li><strong>Typhoid</strong> - Essential for travelers to South Asia, Africa, and Latin America</li>
            <li><strong>Yellow Fever</strong> - Required for entry to many African and South American countries</li>
            <li><strong>Japanese Encephalitis</strong> - Recommended for long-term travelers to rural Asia</li>
            <li><strong>Rabies</strong> - Important for adventure travelers and those visiting areas with limited medical access</li>
            <li><strong>Meningococcal ACYW</strong> - Required for Hajj and Umrah pilgrims, recommended for sub-Saharan Africa</li>
            <li><strong>Cholera (Dukoral)</strong> - Provides protection against cholera and traveler's diarrhea</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <DollarSign className="w-6 h-6 text-cyan-500" />
            Cost of Travel Vaccines in Canada
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Travel vaccines are generally not covered by provincial health insurance plans, though there are some exceptions. Here's what you can expect to pay (prices approximate and may vary by province):
          </p>

          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex justify-between"><span>Hepatitis A</span><span className="font-bold">$70-90 per dose</span></li>
              <li className="flex justify-between"><span>Typhoid (injection)</span><span className="font-bold">$50-70 per dose</span></li>
              <li className="flex justify-between"><span>Yellow Fever</span><span className="font-bold">$150-200 per dose</span></li>
              <li className="flex justify-between"><span>Japanese Encephalitis</span><span className="font-bold">$250-300 per dose</span></li>
              <li className="flex justify-between"><span>Rabies</span><span className="font-bold">$225-250 per dose</span></li>
              <li className="flex justify-between"><span>Meningococcal ACYW</span><span className="font-bold">$100-130 per dose</span></li>
              <li className="flex justify-between"><span>Dukoral (Cholera)</span><span className="font-bold">$60-80 per dose</span></li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Calendar className="w-6 h-6 text-cyan-500" />
            When to Get Vaccinated
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Timing is crucial when it comes to travel vaccinations. Most vaccines require time to build immunity, and some require multiple doses spread over weeks or months:
          </p>

          <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li><strong>Ideal Timeline:</strong> Book your travel health consultation 6-8 weeks before departure</li>
            <li><strong>Minimum Timeline:</strong> Some protection is better than none - even last-minute vaccines can help</li>
            <li><strong>Multi-Dose Vaccines:</strong> Hepatitis B, Japanese Encephalitis, and Rabies require multiple doses over several weeks</li>
            <li><strong>Yellow Fever:</strong> Must be given at least 10 days before entry to endemic countries</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Shield className="w-6 h-6 text-cyan-500" />
            Where to Get Travel Vaccines in Canada
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            You have several options for obtaining travel vaccines in Canada:
          </p>

          <div className="bg-cyan-50 dark:bg-cyan-900/20 border-l-4 border-cyan-500 p-6 mb-6">
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Travel Health Clinics</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Specialized clinics staffed by travel medicine professionals who can provide comprehensive pre-travel consultations and administer vaccines.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-6">
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Virtual Travel Clinics</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Convenient online consultations with travel health experts who can coordinate vaccine administration at local pharmacies and clinics near you.
            </p>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-6 mb-6">
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Pharmacies</h4>
            <p className="text-gray-700 dark:text-gray-300">
              Many pharmacies can administer common travel vaccines like hepatitis A and typhoid, though they may have limited selection.
            </p>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Insurance Coverage</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            While provincial health plans don't typically cover travel vaccines, you may have coverage through:
          </p>

          <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-300 space-y-2">
            <li><strong>Private Health Insurance:</strong> Many employer plans cover 50-100% of travel vaccine costs</li>
            <li><strong>Travel Insurance:</strong> Some policies include pre-travel health consultations</li>
            <li><strong>Health Spending Accounts:</strong> Can often be used for travel vaccines and consultations</li>
            <li><strong>Tax Deductions:</strong> May be eligible as medical expenses if total costs exceed threshold</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Key Takeaways</h3>
          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li>✅ Book your travel health consultation 6-8 weeks before departure</li>
              <li>✅ Budget $200-500+ for travel vaccines depending on destination</li>
              <li>✅ Check if your private insurance covers travel vaccinations</li>
              <li>✅ Consider virtual consultations for convenience and expert guidance</li>
              <li>✅ Keep your vaccination records - some countries require proof of immunization</li>
            </ul>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Protect Your Health for Your Next Trip?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Book a virtual consultation with our travel health experts and get personalized vaccine recommendations.
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="px-8 py-4 bg-white text-cyan-600 font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          >
            Book Virtual Consult
          </button>
        </div>
      </section>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </div>
  );
};

export default TravelVaccinesCanadaGuide;